var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useEffect, forwardRef } from 'react';
import { useTranslate } from 'ra-core';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Fade from '@material-ui/core/Fade';
import LinearProgress from '@material-ui/core/LinearProgress';
import InputLabel from '@material-ui/core/InputLabel';
import Slide from '@material-ui/core/Slide';
import { makeStyles } from '@material-ui/core/styles';
import SaveIcon from '@material-ui/icons/Save';
import { ENTITY_TYPE_HOME } from '../dataProvider/documents';
import { HomePageCreateButton, HomePageEditButton } from './HomePageButton';
import { MarkdownEditor, markdownRenderer as defaultMarkdownRenderer, MarkdownView, } from '../ui';
import { useUserContext } from '../userContexts';
import { useMarkdownContent } from '../documentation';
/**
 * This component is responsible for fetching and displaying an home page content.
 * It also provide mechanisms for portal administrators to update it.
 *
 * The HomePageContent can be used as the default home page if there is only one content to display.
 *
 * @example <caption>Simple usage</caption>
 * <HomePageContent />
 *
 * const MyApp = props => <Admin dashboard={HomePageContent} {...props} />
 *
 */
export const HomePageContent = props => {
    var _a;
    const { navtitle = 'home1', entityUuid = 'home1' } = props, rest = __rest(props, ["navtitle", "entityUuid"]);
    const [{ data, loading }, handleUpdate] = useMarkdownContent({
        entityType: ENTITY_TYPE_HOME,
        entityUuid,
        navtitle,
    });
    const translate = useTranslate();
    const classes = useStyles(rest);
    const [userContext] = useUserContext();
    const canEdit = ((_a = userContext === null || userContext === void 0 ? void 0 : userContext.userDetails) === null || _a === void 0 ? void 0 : _a.portalAdmin) || false;
    const [mode, setMode] = useState('view');
    const handleToggleEditionMode = () => setMode('edition');
    const handleToggleViewMode = () => setMode('view');
    useEffect(() => {
        setMode('view');
    }, [data]);
    if (loading) {
        return (React.createElement(Fade, { in: true, style: {
                transitionDelay: '300ms',
            }, unmountOnExit: true },
            React.createElement(LinearProgress, null)));
    }
    return (React.createElement("div", { className: classes.root },
        React.createElement(MarkdownView, Object.assign({ className: classes.markdown, value: !data
                ? translate('apihub.homepage.placeholder_empty_content')
                : data.markdown }, rest)),
        canEdit ? (React.createElement(React.Fragment, null,
            data ? (React.createElement(HomePageEditButton, { onClick: handleToggleEditionMode })) : (React.createElement(HomePageCreateButton, { onClick: handleToggleEditionMode })),
            React.createElement(HomePageContentEditor, { initialValue: data ? data.markdown : undefined, navtitle: navtitle, onCancel: handleToggleViewMode, onSave: handleUpdate, open: mode === 'edition' }))) : null));
};
const HomePageContentEditor = ({ initialValue, markdownRenderer = defaultMarkdownRenderer, onCancel, onSave, open, navtitle, }) => {
    const classes = useHomePageContentEditorStyles();
    const [value, setValue] = useState(initialValue);
    const translate = useTranslate();
    const handleSave = () => {
        onSave(value);
    };
    const handleCancel = () => {
        setValue(initialValue);
        onCancel();
    };
    useEffect(() => {
        // Be sure the value is updated when the initialValue changed
        setValue(initialValue);
    }, [initialValue]);
    return (React.createElement(Dialog, { open: open, fullScreen: true, onClose: handleCancel, TransitionComponent: Transition },
        React.createElement(DialogTitle, null, navtitle),
        React.createElement(DialogContent, null,
            React.createElement(InputLabel, { shrink: true, htmlFor: "textarea" }, translate('resources.documents.fields.markdown')),
            React.createElement(MarkdownEditor, { className: classes.editor, value: value, onChange: setValue, markdownRenderer: markdownRenderer })),
        React.createElement(DialogActions, { className: classes.actions },
            React.createElement(Button, { color: "primary", variant: "outlined", onClick: handleCancel }, translate('resources.documents.actions.cancel')),
            React.createElement(Button, { color: "primary", variant: "contained", onClick: handleSave, startIcon: React.createElement(SaveIcon, null) }, translate('resources.documents.actions.save')))));
};
const Transition = forwardRef(function Transition(props, ref) {
    return React.createElement(Slide, Object.assign({ direction: "up", ref: ref }, props));
});
const useStyles = makeStyles(theme => ({
    root: {
        position: 'relative',
    },
    markdown: {
        overflowWrap: 'anywhere',
    },
}), {
    name: 'Layer7HomePageContent',
});
const useHomePageContentEditorStyles = makeStyles(theme => ({
    editor: {
        height: `calc(100% - ${theme.spacing(2)}px)`,
    },
    actions: {
        margin: theme.spacing(2),
    },
}), {
    name: 'Layer7HomePageContentEditor',
});
//# sourceMappingURL=HomePageContent.js.map